import { nonnull } from '@/lib/nullish.ts';
import {
  type AvailableDealerIds,
  AvailableDealerIdsSchema,
  type SelectedDealerIds,
  SelectedDealerIdsSchema,
} from '@/models/entities/lead';
import type { Dealers, User } from '@/models/entities/user.ts';

export interface IDealerService {
  getSelectedDealerIds(): SelectedDealerIds;
  setSelectedDealerIds(dealerIds: SelectedDealerIds): void;
  getAvailableDealerIds(): SelectedDealerIds;
  getAvailableDealers(): Dealers;
}

class DealerService implements IDealerService {
  constructor(user: User) {
    this.SelectedDealersKey = `${user.homeAccountId}.selected-dealer-ids`;
    this.AvailableDealerIds = AvailableDealerIdsSchema.parse(
      user.dealerships.map((d) => d.id),
    );
    this.AvailableDealers = user.dealerships;
    this.initWithUser();
  }

  private readonly SelectedDealersKey;
  private readonly AvailableDealerIds: AvailableDealerIds;
  private readonly AvailableDealers: Dealers;

  private initWithUser() {
    if (localStorage.getItem(this.SelectedDealersKey) !== null) {
      try {
        const stored = this.getSelectedDealerIds();
        if (stored.isSubsetOf(this.AvailableDealerIds)) {
          return;
        }
      } catch (error) {
        console.error('State is corrupt: Invalid JSON', error);
      }
    }
    this.setSelectedDealerIds(this.AvailableDealerIds);
  }
  getSelectedDealerIds(): SelectedDealerIds {
    try {
      return SelectedDealerIdsSchema.parse(
        JSON.parse(nonnull(localStorage.getItem(this.SelectedDealersKey))),
      );
    } catch (error) {
      console.error('State is corrupt: Invalid JSON', error);
      localStorage.removeItem(this.SelectedDealersKey);
      throw error;
    }
  }

  getAvailableDealerIds(): SelectedDealerIds {
    return this.AvailableDealerIds;
  }

  getAvailableDealers(): Dealers {
    return this.AvailableDealers;
  }

  setSelectedDealerIds(dealerIds: SelectedDealerIds): void {
    const availableDealerIds = this.getAvailableDealerIds();
    const validatedDealerIds = availableDealerIds.intersection(dealerIds);
    const notOkay = dealerIds.difference(validatedDealerIds);
    if (notOkay.size > 0) {
      throw new Error(
        `Not all dealers are available ${JSON.stringify(notOkay)}`,
      );
    }
    localStorage.setItem(
      this.SelectedDealersKey,
      JSON.stringify(Array.from(validatedDealerIds)),
    );
  }
}

class DealerServiceSingleton implements IDealerService {
  private static instance: IDealerService | null;

  static init(user: User): void {
    DealerServiceSingleton.instance = new DealerService(user);
  }

  private instance(): IDealerService {
    return nonnull(DealerServiceSingleton.instance);
  }

  getSelectedDealerIds(): SelectedDealerIds {
    return this.instance().getSelectedDealerIds();
  }

  setSelectedDealerIds(dealerIds: SelectedDealerIds): void {
    this.instance().setSelectedDealerIds(dealerIds);
  }

  getAvailableDealerIds() {
    return this.instance().getAvailableDealerIds();
  }

  getAvailableDealers() {
    return this.instance().getAvailableDealers();
  }
}

export const InitDealerService = (user: User): void =>
  DealerServiceSingleton.init(user);

export const dealerService: IDealerService = new DealerServiceSingleton();
